import { VersionBadge } from "@/components/ui/documents/version-badge";
import type { VirtualItem } from "@tanstack/react-virtual";
import { ArrowUpRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import type { FlattenedItem } from "./useTableRows";
export const TableFolderVersionRow = observer(function TableFolderVersionRow(
  props: DocumentTablePresentation & { item: FlattenedItem; virtualRow: VirtualItem }
) {
  const { item, virtualRow, measureVirtualRow, versionedFolder } = props;
  if (item.kind !== "folder-version" || !versionedFolder) return null;

  const version = item.version;
  return (
    <div
      key={`${virtualRow.key}-folder-version`}
      data-index={virtualRow.index}
      ref={measureVirtualRow}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      <button
        type="button"
        data-testid="folder-version-row"
        onClick={() => versionedFolder.onOpen(version)}
        className="flex w-full items-center gap-3 border-b bg-muted/10 py-2 pl-10 pr-6 text-left duration-150 ease-out animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/30"
      >
        <VersionBadge version={version.versionNo} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{versionedFolder.title(version)}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {versionedFolder.meta(version)}
            {version.note ? ` · ${version.note}` : ""}
          </span>
        </span>
        <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
    </div>
  );
});

export const TableHistoryLinkRow = observer(function TableHistoryLinkRow(
  props: DocumentTablePresentation & { virtualRow: VirtualItem; item: FlattenedItem }
) {
  const { virtualRow, measureVirtualRow, handleShowVersionHistoryModal, item, t } = props;
  if (item.kind !== "history-link") return null;

  return (
    <div
      key={`${virtualRow.key}-history-link`}
      data-index={virtualRow.index}
      ref={measureVirtualRow}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      <div className="flex items-center justify-end gap-2 border-b bg-muted/10 px-10 py-2 duration-150 ease-out animate-in fade-in-0 slide-in-from-top-1">
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => handleShowVersionHistoryModal(item.versionGroup, item.filename)}
        >
          {t.documents.versioning.history.viewFullHistory}
        </button>
      </div>
    </div>
  );
});
